import React, { useEffect, useContext, useState } from "react";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Table from "components/Table/Table.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Movepoint from "components/Movepoint/Movepoint.js";
import Autosuggest from "react-autosuggest";

// @material-ui/icons
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";

import { AppContext } from "../../AppContext";
import AuthService from "../../AuthService";

const styles = {
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
  spinnerContainer: {
    marginTop: "10px",
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  leftAutoSuggest: {
    marginTop: "10px",
    position: "relative",
    zIndex: "3",
  },
  tabs: {
    marginBottom: "15px",
  },
};

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString();
};

// Live title is shown in the description column: "[Title] - [Description]"
const formatTitleAndDescription = (data) => {
  const title = (data.title || "").trim();
  const description = (data.description || "").trim();
  if (title && description) return `${title} - ${description}`;
  return title || description;
};

export default function Lives() {
  const context = useContext(AppContext);
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const authService = new AuthService();

  let initColumns = [
    {
      columnName: "Creator",
      getData: (data) => data.creatorUserName,
      getCompareData: () => 0,
      isFilterable: true,
      isActive: true,
      isMandatory: true,
    },
    {
      columnName: "Description",
      getData: (data) => formatTitleAndDescription(data),
      getCompareData: () => 0,
      isFilterable: false,
      isActive: true,
      isMandatory: false,
    },
    {
      columnName: "Scheduled",
      getData: (data) => formatDateTime(data.scheduledStart),
      getCompareData: () => 0,
      isFilterable: false,
      isActive: true,
      isMandatory: true,
    },
    {
      columnName: "Start",
      getData: (data) => formatDateTime(data.start),
      getCompareData: () => 0,
      isFilterable: false,
      isActive: true,
      isMandatory: true,
    },
    {
      columnName: "End",
      getData: (data) => formatDateTime(data.end),
      getCompareData: () => 0,
      isFilterable: false,
      isActive: true,
      isMandatory: true,
    },
    {
      columnName: "ID",
      getData: (data) => data.uniqId || "",
      getCompareData: () => 0,
      isFilterable: false,
      isActive: false,
      isMandatory: false,
    },
    {
      columnName: "Spectators",
      getData: (data) =>
        data.spectatorsCount != null ? data.spectatorsCount : "",
      getCompareData: () => 0,
      isFilterable: false,
      isActive: true,
      isMandatory: true,
    },
    {
      columnName: "Min amount",
      getData: (data) => {
        if (data.amountMin == null) return "";
        const currency = data.donationsCurrency
          ? ` ${data.donationsCurrency}`
          : "";
        return `${data.amountMin}${currency}`;
      },
      getCompareData: () => 0,
      isFilterable: false,
      isActive: false,
      isMandatory: false,
    },
    {
      columnName: "Entries",
      getData: (data) => {
        if (data.entriesTotal == null) return "";
        const currency = data.donationsCurrency
          ? ` ${data.donationsCurrency}`
          : "";
        return `${data.entriesTotal}${currency}`;
      },
      getCompareData: () => 0,
      isFilterable: false,
      isActive: true,
      isMandatory: true,
    },
    {
      columnName: "Donations",
      getData: (data) => {
        if (data.donationsTotal == null) return "";
        const currency = data.donationsCurrency
          ? ` ${data.donationsCurrency}`
          : "";
        return `${data.donationsTotal}${currency}`;
      },
      getCompareData: () => 0,
      isFilterable: false,
      isActive: true,
      isMandatory: true,
    },
    {
      columnName: "Gain",
      getData: (data) => {
        const entries =
          data.entriesTotal != null ? Number(data.entriesTotal) : 0;
        const donations =
          data.donationsTotal != null ? Number(data.donationsTotal) : 0;
        const currency = data.donationsCurrency
          ? ` ${data.donationsCurrency}`
          : "";
        return `${entries + donations}${currency}`;
      },
      getCompareData: () => 0,
      isFilterable: false,
      isActive: true,
      isMandatory: false,
    },
  ];

  const [broadcasts, setBroadcasts] = React.useState([]);
  const [columns, setColumns] = React.useState(initColumns);
  const [tableData, setTableData] = useState([]);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(50);
  const [searchLoading, setSearchLoading] = useState(true);
  const [currencies, setCurrencies] = React.useState([]);
  const [tab, setTab] = useState(0);

  // Filter by username
  const [userNameInput, setUserNameInput] = useState("");
  const [userName, setUserName] = useState("");
  const [userNameSuggestions, setUserNameSuggestions] = useState([]);

  useEffect(() => {
    authService.setUrl(
      context.apiUrl + context.categoriesPath + "currencies/all"
    );
    authService.request("get", {}, (response) => {
      setCurrencies(response.data);
    });
  }, []);

  useEffect(() => {
    if (currencies.length > 0) {
      searchBroadcasts(true);
    }
  }, [userName, currencies, tab]);

  const searchMore = (reset) => {
    // Avoid to be triggered immediatly
    if (tableData.length > 0) {
      searchBroadcasts(reset);
    }
  };

  const searchBroadcasts = (reset) => {
    setSearchLoading(true);

    authService.setUrl(
      context.apiUrl +
      context.liveStreamingPath +
      "admin/broadcast-history"
    );
    authService.request(
      "get",
      {
        start: reset ? 0 : start,
        end: reset ? 50 : end,
        username: userName,
        ended: tab === 1,
      },
      (response) => {
        const { data } = response;
        setSearchLoading(false);
        const list = Array.isArray(data) ? data : [];
        let nextBroadcasts = reset ? list : broadcasts.concat(list);
        setBroadcasts(nextBroadcasts);
        let newTabledata = makeDataFromBroadcasts(nextBroadcasts, columns);
        setTableData(newTabledata);
        let newStart = nextBroadcasts.length;
        let newEnd = newStart + 50;
        setStart(newStart);
        setEnd(newEnd);
      }
    );
  };

  const makeDataFromBroadcasts = (items, colums) => {
    var data = [];

    items.forEach((broadcast) => {
      let row = [];
      const currenciesFiltered = currencies.filter(
        (e) => e.code == broadcast.donationsCurrency
      );
      const symbol =
        currenciesFiltered.length > 0 ? currenciesFiltered[0].symbol : "";
      const broadcastWithSymbol = symbol
        ? { ...broadcast, donationsCurrency: symbol }
        : broadcast;

      colums.forEach((column) => {
        if (column.isActive) {
          row.push({
            value: column.getData(broadcastWithSymbol),
            compare: column.getCompareData(broadcastWithSymbol),
          });
        }
      });
      data.push(row);
    });

    return data;
  };

  const onSuggestionSelected = (name) => {
    setUserNameInput(name);
    setUserName(name);
  };

  const userNameInputProps = {
    placeholder: "User Name",
    onChange: (event) => handleUserNameChange(event),
    value: userNameInput,
    style: {
      padding: "17.75px 20px",
      width: "315px",
      boxSizing: "border-box",
    },
  };

  const handleUserNameChange = (event) => {
    let username = event.target.value;
    if (username != undefined) {
      setUserNameInput(username);
      if (userName) setUserName("");

      authService.setUrl(context.apiUrl + context.usersHonoPath + "suggestion");
      authService.request(
        "get",
        {
          name: username,
          show: "All",
          age: "[18,60]",
          distance: 100,
          country: "All",
          sex: "All",
          userType: "All",
          radarVisibilityOnly: false,
        },
        (response) => {
          const { data } = response;
          setUserNameSuggestions(data);
        }
      );
    }
  };

  const toggleColumn = (index) => {
    const nextColumns = columns.map((column, i) => {
      if (i === index && !column.isMandatory) {
        return { ...column, isActive: !column.isActive };
      }
      return column;
    });
    setColumns(nextColumns);
    setTableData(makeDataFromBroadcasts(broadcasts, nextColumns));
  };

  return (
    <>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="primary">
              <h4 className={classes.cardTitleWhite}>Lives</h4>
              <p className={classes.cardCategoryWhite}>
                {tab === 0
                  ? "Here are all ongoing live broadcasts"
                  : "Here are all past live broadcasts"}
              </p>
            </CardHeader>
            <CardBody>
              <AppBar position="static" color="default" className={classes.tabs}>
                <Tabs
                  value={tab}
                  onChange={(event, newTab) => setTab(newTab)}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  <Tab label="Ongoing" />
                  <Tab label="Past" />
                </Tabs>
              </AppBar>
              {columns.map((column, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      disabled={column.isMandatory}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                      checked={column.isActive}
                      onChange={() => toggleColumn(index)}
                      color="secondary"
                    />
                  }
                  label={column.columnName}
                />
              ))}

              <div className={classes.leftAutoSuggest}>
                <Autosuggest
                  suggestions={userNameSuggestions.filter(
                    (e) =>
                      e?.toLowerCase().indexOf(userNameInput?.toLowerCase()) !==
                      -1
                  )}
                  onSuggestionsFetchRequested={() => { }}
                  onSuggestionsClearRequested={() => { }}
                  onSuggestionSelected={(event, data) =>
                    onSuggestionSelected(data.suggestionValue)
                  }
                  getSuggestionValue={(suggestion) => suggestion}
                  shouldRenderSuggestions={() => true}
                  renderSuggestion={(elmt) => <span>{elmt}</span>}
                  inputProps={userNameInputProps}
                />
              </div>

              <Table
                tableHeaderColor="primary"
                tableHead={columns
                  .filter((column) => column.isActive)
                  .map((column) => column.columnName)}
                tableData={tableData.map((e) => e.map((e) => e.value))}
              />

              {searchLoading && (
                <div className={classes.spinnerContainer}>
                  <CircularProgress color="secondary" />
                </div>
              )}
              <Movepoint
                onEnter={() => {
                  searchMore(false);
                }}
                onLeave={() => { }}
              />
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </>
  );
}
